import { darkPalette } from './darkPalette'

export const colors = {
  brand: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#2563eb',
    600: '#1d4ed8',
    700: '#1e40af',
    800: '#1e3a8a',
    900: '#172554',
  },
  gray: {
    50: '#fafaf8',
    100: '#f3f2ef',
    200: '#e7e5e0',
    300: '#d5d2cc',
    400: '#a8a39b',
    500: '#78726a',
    600: '#57514a',
    700: '#403b35',
    800: '#2a2621',
    900: '#1b1815',
  },
  green: {
    400: darkPalette.greenSoft,
    500: darkPalette.greenPrimary,
  },
}

export const config = {
  initialColorMode: 'light',
  useSystemColorMode: true,
}

// Flat dotted keys (not nested objects): Chakra's semantic-token flattener only stops
// recursion when every key in an object is a pseudo-selector or `default`. A nested
// `surface: { default: 'white', muted: 'gray.50' }` would never register `surface`
// itself as a token, since `muted` isn't a recognized condition key. Keep these flat.
//
// `_dark` values come from `darkPalette` (see darkPalette.ts). Light `default` values are
// unchanged so light mode renders identically.
export const semanticTokens = {
  colors: {
    surface: { default: 'white', _dark: darkPalette.bgCard },
    'surface.muted': { default: 'gray.50', _dark: darkPalette.bgCardRaised },
    border: { default: 'gray.200', _dark: darkPalette.borderSubtle },
    'border.strong': { default: 'gray.300', _dark: darkPalette.dividerStrong },
    'border.control': { default: 'gray.200', _dark: darkPalette.borderControl },
    divider: { default: 'gray.200', _dark: darkPalette.dividerSubtle },
    text: { default: 'gray.900', _dark: darkPalette.textPrimary },
    'text.muted': { default: 'gray.500', _dark: darkPalette.textMuted },
    'text.subtle': { default: 'gray.400', _dark: darkPalette.textDisabled },
    'text.secondary': { default: 'gray.600', _dark: darkPalette.textSecondary },
    'text.secondary.strong': { default: 'gray.700', _dark: darkPalette.textPrimary },
    'tint.cyan50': { default: 'cyan.50', _dark: darkPalette.bgCardRaised },
    'tint.cyan100': { default: 'cyan.100', _dark: darkPalette.bgTableHeader },
    'tint.gray100': { default: 'gray.100', _dark: darkPalette.bgCardRaised },
    'tint.orange50': { default: 'orange.50', _dark: darkPalette.bgCardRaised },
    'tint.orange100': { default: 'orange.100', _dark: darkPalette.bgTableHeader },
    'tint.green50': { default: 'green.50', _dark: 'rgba(75, 189, 99, 0.12)' },
    'tint.purple50': { default: 'purple.50', _dark: 'rgba(168, 85, 247, 0.14)' },
    'text.purple': { default: 'purple.600', _dark: 'purple.300' },
    // Accent ramp (dark): bluePrimary = accent / accent.strong; blueSoft = accent.soft / nav.*
    accent: { default: 'brand.500', _dark: darkPalette.bluePrimary },
    'accent.strong': { default: 'brand.600', _dark: darkPalette.blueAction },
    'accent.soft': { default: 'brand.400', _dark: darkPalette.blueSoft },
    'nav.active.bg': { default: 'brand.50', _dark: darkPalette.blueActiveBg },
    'nav.active.text': { default: 'brand.700', _dark: darkPalette.blueSoft },
    /** Inactive nav row hover — matches active text in dark mode for one consistent blue. */
    'nav.hover.text': { default: 'brand.600', _dark: darkPalette.blueSoft },
    'scroll.edge.fade': { default: 'rgba(15, 23, 42, 0.06)', _dark: 'rgba(0, 0, 0, 0.10)' },
    'scroll.edge.fade.mid': { default: 'rgba(15, 23, 42, 0.025)', _dark: 'rgba(0, 0, 0, 0.04)' },
    'scroll.edge.shadow': { default: 'rgba(15, 23, 42, 0.08)', _dark: 'rgba(0, 0, 0, 0.16)' },
    'app.bg': { default: '#f4f8fc', _dark: darkPalette.bgApp },
    'main.bg': { default: '#f4f8fc', _dark: darkPalette.bgMain },
    'topbar.bg': { default: 'white', _dark: darkPalette.bgTopbar },
    'sidebar.bg': { default: 'white', _dark: darkPalette.bgSidebar },
    'icon.bg': { default: 'brand.50', _dark: darkPalette.blueActiveBg },
    'table.header.bg': { default: 'brand.50', _dark: darkPalette.bgTableHeader },
    'chart.bar': { default: 'brand.500', _dark: darkPalette.bluePrimary },
    'chart.grid': { default: 'gray.100', _dark: darkPalette.dividerSubtle },
    'status.success': { default: 'green.500', _dark: darkPalette.greenPrimary },
    'status.success.soft': { default: 'green.400', _dark: darkPalette.greenSoft },
    'text.danger': { default: 'red.600', _dark: 'red.300' },
    'status.danger.bg': { default: 'red.50', _dark: 'rgba(239, 68, 68, 0.12)' },
    'status.danger.border': { default: 'red.200', _dark: 'red.500' },
    'status.warning': { default: 'orange.500', _dark: 'orange.300' },
    'skeleton.start': { default: 'gray.100', _dark: darkPalette.bgCardRaised },
    'skeleton.end': { default: 'gray.300', _dark: darkPalette.dividerStrong },
    'spinner.track': { default: 'gray.200', _dark: darkPalette.bgCardRaised },
    'queue.panel.border': { default: 'gray.200', _dark: darkPalette.queuePanelBorder },
    'queue.panel.bg': { default: 'white', _dark: darkPalette.queuePanelBgTop },
    'queue.search.bg': { default: 'white', _dark: darkPalette.queueSearchBg },
    'queue.search.border': { default: 'gray.200', _dark: darkPalette.queueSearchBorder },
    'queue.expanded.bg': { default: 'gray.50', _dark: darkPalette.queueExpandedBg },
    'queue.avatar.bg': { default: 'brand.50', _dark: darkPalette.queueAvatarBg },
    'queue.consult.bg': { default: 'brand.100', _dark: darkPalette.queueConsultBtnBg },
    'queue.consult.text': { default: 'brand.900', _dark: darkPalette.queueConsultBtnText },
    'queue.consult.border': { default: 'brand.200', _dark: darkPalette.queueConsultBtnBorder },
    'queue.metric.blue.icon': { default: 'brand.700', _dark: darkPalette.queueMetricBlueIcon },
    'queue.metric.blue.bg': { default: 'brand.100', _dark: darkPalette.queueMetricBlueBg },
    'queue.metric.yellow.icon': { default: 'yellow.700', _dark: darkPalette.queueMetricYellowIcon },
    'queue.metric.yellow.bg': { default: 'yellow.100', _dark: darkPalette.queueMetricYellowBg },
    'queue.metric.green.icon': { default: 'green.700', _dark: darkPalette.queueMetricGreenIcon },
    'queue.metric.green.bg': { default: 'green.100', _dark: darkPalette.queueMetricGreenBg },
    'queue.metric.purple.icon': { default: 'purple.700', _dark: darkPalette.queueMetricPurpleIcon },
    'queue.metric.purple.bg': { default: 'purple.100', _dark: darkPalette.queueMetricPurpleBg },
    'queue.badge.waiting.bg': { default: 'yellow.100', _dark: darkPalette.queueWaitingBadgeBg },
    'queue.badge.waiting.text': { default: 'yellow.800', _dark: darkPalette.queueWaitingBadgeText },
    'queue.badge.waiting.border': { default: 'yellow.400', _dark: darkPalette.queueWaitingBadgeBorder },
    'queue.badge.scheduled.bg': { default: 'blue.100', _dark: darkPalette.queueScheduledBadgeBg },
    'queue.badge.scheduled.text': { default: 'blue.800', _dark: darkPalette.queueScheduledBadgeText },
    'queue.badge.scheduled.border': { default: 'blue.400', _dark: darkPalette.queueScheduledBadgeBorder },
    'queue.badge.next.bg': { default: 'green.100', _dark: darkPalette.queueNextBadgeBg },
    'queue.badge.next.text': { default: 'green.800', _dark: darkPalette.queueNextBadgeText },
    'queue.badge.next.border': { default: 'green.400', _dark: darkPalette.queueNextBadgeBorder },
  },
}
